import { apiClient } from './api';

export type SupportTicketStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
export type SupportTicketPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export interface SupportTicket {
  id: string;
  userId: string;
  orderId: string | null;
  subject: string;
  description: string;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  assignedTo: string | null;
  resolutionNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedTickets {
  tickets: SupportTicket[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateTicketDto {
  orderId?: string;
  subject: string;
  description: string;
  priority?: SupportTicketPriority;
}

export interface AdminUpdateTicketDto {
  status?: SupportTicketStatus;
  priority?: SupportTicketPriority;
  assignedTo?: string;
  resolutionNotes?: string;
}

export interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
  urgent: number;
}

export const supportService = {
  async createTicket(dto: CreateTicketDto): Promise<SupportTicket> {
    const response = await apiClient.post('/support/tickets', dto);
    return response.data.data;
  },

  async getMyTickets(params?: {
    status?: SupportTicketStatus;
    page?: number;
    limit?: number;
  }): Promise<PaginatedTickets> {
    const response = await apiClient.get('/support/tickets', { params });
    return response.data.data;
  },

  async getMyTicketById(ticketId: string): Promise<SupportTicket> {
    const response = await apiClient.get(`/support/tickets/${ticketId}`);
    return response.data.data;
  },

  async closeMyTicket(ticketId: string): Promise<SupportTicket> {
    const response = await apiClient.patch(`/support/tickets/${ticketId}/close`);
    return response.data.data;
  },
};

export const adminSupportService = {
  async getStats(): Promise<TicketStats> {
    const response = await apiClient.get('/admin/support/tickets/stats');
    return response.data.data;
  },

  async listTickets(params?: {
    status?: SupportTicketStatus;
    priority?: SupportTicketPriority;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedTickets> {
    const response = await apiClient.get('/admin/support/tickets', { params });
    return response.data.data;
  },

  async getTicketById(ticketId: string): Promise<SupportTicket> {
    const response = await apiClient.get(`/admin/support/tickets/${ticketId}`);
    return response.data.data;
  },

  async updateTicket(ticketId: string, dto: AdminUpdateTicketDto): Promise<SupportTicket> {
    const response = await apiClient.patch(`/admin/support/tickets/${ticketId}`, dto);
    return response.data.data;
  },
};
